import { useState } from "react";
import { Check, Copy, Download, Printer, Loader2 } from "lucide-react";

/**
 * The QR code departments put on their noticeboards.
 *
 * It encodes one unchanging public address — the pledge form — so there is
 * nothing personal in it and the same code serves every student and every
 * cohort. Printing it once is the point: a department pins it up, students
 * photograph it, and nobody types a URL.
 *
 * Generated through api.qrserver.com, the same service the event access cards
 * already use (`events/components/AccessCard.tsx`). Introducing a second
 * mechanism for the same job would mean two things to maintain and two places
 * for the QR to look different. The data sent is a public URL, which is less
 * than the access cards send.
 *
 * Because that is an outside service, the panel offers **Download** and
 * **Print**: once the Secretariat has the PNG or the printed sheet, the notice
 * works whether or not the service is reachable.
 */

/** The QR image for a URL, at a given pixel size. */
export function qrSrc(url: string, size = 240) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(url)}`;
}

function esc(s: string) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

/**
 * Opens a printable A4 notice: NIQS letterhead, the QR, the address under it
 * in case a phone will not scan, and the four things a student has to do.
 *
 * Written as a whole document into a new window rather than printing the
 * portal page, so what comes out of the printer is the notice and nothing
 * else — no sidebar, no buttons, no filters.
 */
export function printPledgeNotice(url: string, opts: { cohortName?: string; closesOn?: string; ceremonyDate?: string }) {
  const w = window.open("", "_blank", "width=820,height=1040");
  if (!w) return false;

  const when = [
    opts.closesOn ? `Form closes: <strong>${esc(opts.closesOn)}, 23:59</strong>` : "",
    opts.ceremonyDate ? `Pledge Ceremony: <strong>${esc(opts.ceremonyDate)}</strong>` : "",
  ].filter(Boolean).join(" &nbsp;·&nbsp; ");

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>NIQS Graduate Pledge — notice</title>
  <style>
    @page { size: A4; margin: 18mm; }
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif;}
    body{color:#14181f;text-align:center;}
    .logo{height:84px;object-fit:contain;margin-bottom:14px;}
    .org{font-size:11px;font-weight:900;letter-spacing:.24em;text-transform:uppercase;color:#6b7280;}
    h1{font-size:30px;color:#0b2e6b;margin:10px 0 4px;line-height:1.2;}
    .sub{font-size:15px;color:#4b5563;margin-bottom:6px;}
    .when{font-size:13px;color:#4b5563;margin-bottom:22px;}
    .qr{width:300px;height:300px;border:1px solid #d8dee7;border-radius:12px;padding:10px;}
    .scan{font-size:14px;font-weight:700;color:#0b2e6b;margin:14px 0 4px;}
    .url{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:14px;color:#14181f;
         border:1px dashed #b9c2cf;border-radius:8px;padding:9px 14px;display:inline-block;margin-bottom:24px;}
    ol{display:inline-block;text-align:left;font-size:14px;line-height:2;color:#23303f;}
    .foot{margin-top:26px;font-size:11px;color:#8d98a8;line-height:1.6;}
    @media print{ .noprint{display:none;} }
    .noprint{margin-top:22px;}
    .noprint button{font-size:14px;padding:9px 22px;border-radius:7px;border:0;background:#0b2e6b;color:#fff;cursor:pointer;}
  </style>
</head>
<body>
  <img class="logo" src="${esc(window.location.origin)}/niqs-logo.png" alt="NIQS"
       onerror="this.style.display='none'" />
  <div class="org">The Nigerian Institute of Quantity Surveyors</div>
  <h1>Graduating Students&rsquo;<br>Declaration of Intent</h1>
  <div class="sub">${esc(opts.cohortName || "Graduate Pledge")}</div>
  ${when ? `<div class="when">${when}</div>` : ""}

  <img class="qr" src="${esc(qrSrc(url, 600))}" alt="QR code for the Graduate Pledge form" />

  <div class="scan">Scan this code with your phone camera</div>
  <div class="url">${esc(url)}</div>

  <ol>
    <li>Complete the form online &mdash; no login needed.</li>
    <li>Print the form you are sent and sign Sections C and D.</li>
    <li>Get your Head of Department&rsquo;s signature and stamp in Section E.</li>
    <li>Upload a photo of the signed form, then bring the original to the ceremony.</li>
  </ol>

  <div class="foot">
    Completing this form does not by itself confer membership of the Institute.<br>
    Enquiries: info@niqs.org.ng
  </div>

  <div class="noprint"><button onclick="window.print()">Print this notice</button></div>
</body>
</html>`);
  w.document.close();
  return true;
}

export default function PledgeQrPanel({
  url, cohortName, closesOn, ceremonyDate,
}: {
  url: string;
  cohortName?: string;
  closesOn?: string;
  ceremonyDate?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy — the link is shown above, copy it by hand.");
    }
  };

  /**
   * Saves the PNG rather than linking straight to the service: a plain
   * `download` attribute is ignored on a cross-origin URL, so the file would
   * open in a tab instead of saving.
   */
  const download = async () => {
    setDownloading(true);
    setError("");
    try {
      const res = await fetch(qrSrc(url, 1000));
      if (!res.ok) throw new Error();
      const blobUrl = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "niqs-graduate-pledge-qr.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setError("The QR image could not be downloaded. Check the connection, or use Print notice.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-xl border bg-slate-50 p-4 flex flex-col sm:flex-row gap-4">
      <img
        src={qrSrc(url, 240)}
        alt="QR code for the Graduate Pledge form"
        className="w-32 h-32 shrink-0 rounded-lg border bg-white p-1.5 self-center sm:self-start"
      />

      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Student form — share this
        </div>
        <p className="text-xs text-slate-500 mt-1 mb-2">
          Put the code on departmental notices, or send the link to class groups. It is the
          same every year and contains nothing personal.
        </p>

        <code className="block text-[11px] font-mono text-[#0b2e6b] bg-white border rounded px-2 py-1.5 break-all mb-3">
          {url}
        </code>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={copy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white text-sm text-gray-600 hover:bg-gray-50"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
          <button
            onClick={download}
            disabled={downloading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download QR
          </button>
          <button
            onClick={() => {
              if (!printPledgeNotice(url, { cohortName, closesOn, ceremonyDate })) {
                setError("Your browser blocked the print window. Allow pop-ups for this site.");
              }
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0b2e6b] text-white text-sm font-semibold hover:bg-[#0a2558]"
          >
            <Printer className="w-4 h-4" /> Print notice
          </button>
        </div>

        {error && <p className="text-[11px] text-red-600 mt-2">{error}</p>}
      </div>
    </div>
  );
}
